import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Checkbox, Form, Input, InputNumber, Modal, Radio, Select, Tooltip, message } from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { ChtType, DashboardSettingType, DashboardSources, QueryExpression } from '../../constants/DashboardConf'
import { fetchPii, fetchSchema } from '../../apis/source'
import ProfileSetting from './components/Profile'
import PIISetting from './components/PII'
import FootprintSetting from './components/Footprint'
import SummarySetting from './components/Summary'
import ChartSetting from './components/Chart'
import { BasicSettingWrapper, CustomSettingWrapper, FormButton, SettingWrapper } from './dashboard.styled'
import { chtOptions, config, constructCrossDataForm, constructFormEditMode, constructToSave, crossDataFormatSave, pieAndBarFields } from './Conf'
import { fetchDataSourceTree } from '../../apis/dashboard'
import _ from 'lodash'
import StyleSetting from './components/StyleSetting'
import CrossDataSetting from './components/CrossData'
import SankeySetting from './components/Sankey'
import { TimelineExtraSetting, TimelineSetting } from './components/Timeline'
import IndicatorSetting from './components/Indicator'
import CustomSetting from './components/Custom'
import { fetchDimension } from '../../apis/indicator'

const { PII, PROFILE, FOOTPRINT, SUMMARY, PIECHART, BARCHART, LINECHART, MAP, SANKEY, TIMELINE, CUSTOM, TAGS } = DashboardSettingType
const { DATASOURCE, THIRD_PARTY, TAXONOMY, PII_TABLE, INDICATOR, FIRSTPARTY_TAXONOMY, THIRDPARTY_TAXONOMY } = DashboardSources
const { AGE, GENDER, HOME, WORK, INTEREST, WEBSITE, APP } = ChtType

function SettingModal (props) {
  const { data, show, setShow, dataSrcList } = props
  const { t: ln_t } = useTranslation()
  const { columnId, sort, label, type, category, brandId, groupId, addMode, successCallback, editData } = data || {}
  const [form] = Form.useForm()

  const [loading, setLoading] = useState(false)
  const [categoryDetail, setCategoryDetail] = useState(DATASOURCE)
  const [counter, setCounter] = useState([])
  const [rawSchema, setRawSchema] = useState({})
  const [schema, setSchema] = useState([])
  const [piiSchema, setPiiSchema] = useState([])
  const [dataSourceTree, setDataSourceTree] = useState([])
  const [dimensions, setDimensions] = useState([])
  const [indicators, setIndicators] = useState([])
  const [crossData, setCrossData] = useState(false)
  const [crossDataStep, setCrossDataStep] = useState(1)
  const [crossDataCount, setCrossDataCount] = useState([1])
  const [crossDataSchema, setCrossDataSchema] = useState([])
  const [crossDataFormVal, setCrossDataFormVal] = useState({})
  const [anyCrossDataChange, setAnyCrossDataChange] = useState(false)
  const formattedSchema = schema?.map(i => ({ value: i.columnKeyName || i.columnName, label: i.columnName }))
  const conf = { ...config.default, ...config[category] || {} }
  const { addApi, modApi } = conf?.apis
  const dataSourceTableMapping = dataSourceTree?.reduce((all, i) => ({ ...all, [i.dataSourceId]: i.tables?.map(t => ({ ...t, value: t.tableName, dataSourceId: i.dataSourceId })) }), {}) || []
  const uniqueDataSrcTableKeys = dataSourceTree?.map(i => i.tables?.map(j => `${i.dataSourceId}-${j.tableName}`))?.flat() || []
  const tableJoinKeyMapping = Object.values(dataSourceTableMapping)?.flat()?.reduce((all, i) => ({ ...all, [`${i.dataSourceId}-${i.tableName}`]: i.columns?.map(t => ({ ...t, value: t.columnKeyName || i.columnName, label: t.columnName, dataSourceId: i.dataSourceId })) }), {}) || []
  const allTableKeyMapping = Object.values(tableJoinKeyMapping)?.flat()?.reduce((all, i) => ({ ...all, [`${i.dataSourceId}^@^${i.tableName}^@^${i.columnKeyName || i.columnName}`]: i }), {}) || []
  const usedSchema = conf.chartSchema === PII_TABLE ? piiSchema : schema
  const columnMapping = crossData || type === SANKEY || type === TIMELINE ? allTableKeyMapping : usedSchema?.reduce((all, i) => ({ ...all, [i.columnKeyName || i.columnName]: i }), {})
  const indicatorMapping = indicators?.reduce((all, i) => ({ ...all, [i.id]: i }), {})

  const sortOptions = [{ value: 'ASC', label: ln_t('dashboard.asc') }, { value: 'DESC', label: ln_t('dashboard.desc') }]
  const categoryDetailLabel = {
    [DATASOURCE]: ln_t('dashboard.firstParty'),
    [THIRD_PARTY]: ln_t('dashboard.thirdParty'),
    [TAXONOMY]: ln_t('dashboard.taxonomy'),
    [INDICATOR]: ln_t('dashboard.indicator'),
    [FIRSTPARTY_TAXONOMY]: ln_t('dashboard.firstParty'),
    [THIRDPARTY_TAXONOMY]: ln_t('dashboard.thirdParty')
  }
  const chtOptionsLabel = {
    [AGE]: ln_t('dashboard.age'),
    [GENDER]: ln_t('dashboard.gender'),
    [HOME]: ln_t('dashboard.home'),
    [WORK]: ln_t('dashboard.work'),
    [INTEREST]: ln_t('dashboard.interest'),
    [WEBSITE]: ln_t('dashboard.website'),
    [APP]: 'APP'
  }

  const defPlaceholder = (name, type) => {
    const message = type === 'input' ? ln_t('util.pleaseEnter') : type === 'select' ? ln_t('util.pelaseSelect') : ''
    return `${message} ${ln_t(`dashboard.${name}`)}`
  }
  const defConfig = (name, type, required = true) => ({ name, label: ln_t(`dashboard.${name}`), rules: [{ required, message: defPlaceholder(name, type) }] })

  const handleDelCounter = key => {
    setCounter(prev => prev.filter(i => i !== key))
    form.setFieldsValue({ [`${key}-column`]: undefined, [`${key}-value`]: undefined, [`${key}-operator`]: undefined })
  }
  const handleAddCounter = () => setCounter(prev => [...prev, counter.length ? Math.max(...counter) + 1 : 1])

  // ----- Cross Data ----- //
  const resetPieBarSetting = () => {
    const counterValReset = counter?.reduce((all, i) => ({ ...all, [`${i}-column`]: undefined, [`${i}-value`]: undefined, [`${i}-operator`]: undefined, [`${i}-rename`]: undefined }), {})
    const fieldsReset = pieAndBarFields?.reduce((all, i) => ({ ...all, [i]: undefined }), {})
    form.setFieldsValue({ ...fieldsReset, ...counterValReset })
    if (type === CUSTOM) setCounter([1])
  }
  const resetCrossData = () => {
    setCrossData(false)
    setCrossDataStep(1)
    resetPieBarSetting()
  }

  const handleCheckCrossData = e => {
    setCrossData(e.target.checked)
    setCrossDataStep(1)
    resetPieBarSetting()
  }

  const getCrossDataKeys = fields => crossDataCount.map(sequence => fields?.map(field => ([`${sequence}-parent-${field}`, `${sequence}-child-${field}`]))).flat(Infinity)
  const validateCrossData = async (fields = ['dataSource', 'tableName', 'joinKey']) => await form.validateFields(getCrossDataKeys(fields))

  const handleCrossDataNext = async () => {
    await validateCrossData()
    const formVal = form.getFieldsValue() || {}
    const dataSourcesName = dataSourceTree?.map(i => ({ key: i.dataSourceId, val: i.dataSourceName }))?.reduce((all, { key, val }) => ({ ...all, [key]: val }), {}) || {}
    const mapping = {}
    getCrossDataKeys(['dataSource'])?.forEach(key => {
      const id = formVal[key]
      if (!mapping[id]) mapping[id] = { value: id, label: dataSourcesName[id], children: [] }
      mapping[id].children = [...mapping[id].children, { value: formVal[key?.replace('dataSource', 'tableName')] }]
    })
    const formattedSchema = Object.values(mapping)?.map(i => {
      const groupByTable = Object.groupBy(i.children, ({ value }) => value)
      const children = Object.entries(groupByTable)?.map(([key, val]) => ({ value: key, label: key, children: tableJoinKeyMapping[`${i.value}-${key}`] }))
      return { ...i, children }
    })
    setCrossDataSchema(formattedSchema)
    setCrossDataFormVal(formVal)
    setCrossDataStep(2)
    if (anyCrossDataChange) {
      resetPieBarSetting()
      setAnyCrossDataChange(false)
    }
    if (type === CUSTOM && !counter.length) setCounter([1])
  }
  const handleCrossDataPrevious = () => {
    setCrossDataStep(1)
  }
  // ---------------------- //

  const handleCatDetailChange = async val => {
    setCategoryDetail(val)
    const newFields = { title: form.getFieldValue('title'), categoryDetail: val }
    form.resetFields()
    form.setFieldsValue(newFields)
    resetCrossData()
  }
  const handleDataSrcChange = async dataSrc => {
    const newFields = { title: form.getFieldValue('title'), categoryDetail: form.getFieldValue('categoryDetail'), dataSource: dataSrc }
    form.resetFields()
    form.setFieldsValue(newFields)
    const res = await fetchSchema(dataSrc)
    if (res?.status === 200) {
      const data = Object.entries(res?.data).reduce((all, [key, val]) => ({ ...all, [key]: val.map(i => ({ ...i, columnKeyName: i?.columnName, columnName: i?.alias })) }), {})
      setRawSchema(data)
    }
    return data
  }
  const handleTableChange = async (table, sch = rawSchema) => setSchema(sch[table] || [])

  const handleModalClose = () => {
    setShow(false)
    setLoading(false)
    form.resetFields()
    setCounter([])
    setPiiSchema([])
    setSchema([])
    setRawSchema({})
    setCategoryDetail(DATASOURCE)
    resetCrossData()
    setCrossDataCount([1])
  }

  const handleAdd = async reqBody => {
    setLoading(true)
    const res = await addApi(reqBody)
    if (res?.status === 200 || res?.status === 201) {
      message.success(ln_t('message.success.create'))
      handleModalClose()
      successCallback()
    }
    setLoading(false)
  }
  const handleEdit = async reqBody => {
    setLoading(true)
    const res = await modApi(editData?.id, reqBody)
    if (res?.status === 200) {
      message.success(ln_t('message.success.modify'))
      handleModalClose()
      successCallback()
    }
    setLoading(false)
  }

  const handleSave = async value => {
    setLoading(true)
    const { title, settingFor, categoryDetail, chartType, dataSource: dataSourceId = 0, tableName = '', themeColor, chartStyle, ...other } = value
    const isIndicator = categoryDetail === INDICATOR
    const { UISetting, query, error } = constructToSave[isIndicator ? INDICATOR : type]?.(other, isIndicator ? indicatorMapping : columnMapping, dataSourceTree) || {}
    if (error) {
      setLoading(false)
      return message.error(ln_t(error))
    }
    const style = { themeColor: type === MAP ? themeColor.toRgbString() : themeColor, chartStyle }
    const joinDataSource = crossData ? crossDataFormatSave(crossDataFormVal, crossDataCount) : {}
    const reqBody = {
      title,
      category,
      categoryDetail: type === TAGS ? null : (categoryDetail || (type === MAP ? THIRD_PARTY : DATASOURCE)),
      ruleType: type === TAGS ? categoryDetail : type,
      position: { ...editData?.position || {}, column: columnId, sort },
      groupId,
      brandId,
      dataSourceId,
      tableName,
      status: 1,
      crossDataCalculate: crossData ? 1 : 0,
      UISetting: { ...UISetting, ...joinDataSource, style },
      query: { rules: query, ...joinDataSource }
    }
    // console.log(reqBody)
    // if (!reqBody) addMode ? handleAdd(reqBody) : handleEdit(reqBody)
    addMode ? handleAdd(reqBody) : handleEdit(reqBody)
    setLoading(false)
  }

  const getPIIandMapping = async () => {
    const res = await fetchPii(brandId, { maxResults: 999 })
    if (res?.status === 200) setPiiSchema(res?.data || {})
  }
  const setup = async () => {
    const { ruleType, dataSourceId, tableName, categoryDetail, UISetting, crossDataCalculate, ...other } = editData || {}
    const { style, joinDataSource, type, ...forSetup } = UISetting || {}
    const isIndicator = categoryDetail === INDICATOR
    const { customVal, counterVal, needSetCounter } = constructFormEditMode[isIndicator ? INDICATOR : ruleType]?.(forSetup) || {}
    const category = type === TAGS ? ruleType : categoryDetail
    setCategoryDetail(category)
    const sch = dataSourceId === 0 ? {} : await handleDataSrcChange(dataSourceId, true)
    handleTableChange(tableName, sch)
    if (needSetCounter) setCounter(counterVal)
    let crossDataVal = {}
    if (crossDataCalculate) {
      setCrossData(crossDataCalculate)
      setCrossDataCount(joinDataSource.map(i => i.sequence + 1))
      crossDataVal = constructCrossDataForm(joinDataSource)
    }
    form.setFieldsValue({ ...other, dataSource: dataSourceId, tableName, categoryDetail: category, ...customVal, ...style, ...crossDataVal })
  }

  const getDataSourceTree = async () => {
    const res = await fetchDataSourceTree(data?.brandId)
    if (res?.status === 200) {
      setDataSourceTree(res?.data?.filter(i => i.dataSourceId)?.map(i => ({
        ...i,
        tables: i.tables?.map(table => ({ ...table, columns: table?.columns?.map(col => ({ ...col, columnKeyName: col?.columnName, columnName: col?.alias })) }))
      })))
    }
  }

  const getDimension = async () => {
    const res = await fetchDimension({ brandId: data?.brandId, maxResults: 5000 })
    if (res?.status === 200) setDimensions(res?.data?.filter(i => i.columnName?.length === 1))
  }

  useEffect(() => {
    if (!show) return
    if (type === PII || type === CUSTOM) setCounter([1])
    if (!conf.useDataSource.includes(type) || conf.chartSchema === PII_TABLE) getPIIandMapping()
    if (conf.chartSchema === PII_TABLE) form.setFieldValue('aggregate', QueryExpression.COUNT)
    if (conf.useDataSourceTree.includes(type)) getDataSourceTree()
    if (conf?.useCategoryDetail?.includes(type)) getDimension()

    form.setFieldValue('categoryDetail', type === MAP ? THIRD_PARTY : type === TAGS ? FIRSTPARTY_TAXONOMY : DATASOURCE)
    if (type === MAP && addMode) setCategoryDetail(THIRD_PARTY)
    if (type === TAGS && addMode) setCategoryDetail(FIRSTPARTY_TAXONOMY)

    if (addMode) return
    setup()
  }, [show])

  const OrderAndLimit = <>
    <Form.Item {...defConfig('order', 'select')} style={{ width: '100%' }}>
      <Select showSearch placeholder={defPlaceholder('order', 'select')} options={sortOptions} optionFilterProp="children" />
    </Form.Item>
    <Form.Item {...defConfig('limit', 'input', false)} style={{ width: '100%' }}>
      <InputNumber placeholder={defPlaceholder('limit', 'input')} precision={0} min={0} style={{ width: '100%' }} />
    </Form.Item>
  </>

  const customConf = { usePiiSchema: conf.chartSchema === PII_TABLE }
  const passProps = { ...customConf, data, show, form, defPlaceholder, defConfig, counter, setCounter, schema: formattedSchema, piiSchema, sortOptions, handleAddCounter, handleDelCounter, crossData, crossDataSchema, columnMapping, editData }
  const crossDataProps = { defConfig, defPlaceholder, form, dataSourceTree, validateCrossData, count: crossDataCount, setCount: setCrossDataCount, dataSourceTableMapping, uniqueDataSrcTableKeys, tableJoinKeyMapping, setAnyCrossDataChange, useJoinLogic: true }
  const indicatorProps = { dimensions, indicators, setIndicators, indicatorMapping, type, OrderAndLimit }
  const components = {
    [PROFILE]: <ProfileSetting {...passProps} />,
    [PII]: <PIISetting {...passProps} />,
    [FOOTPRINT]: <FootprintSetting {...passProps} />,
    [SUMMARY]: <SummarySetting {...passProps} />,
    [PIECHART]: <ChartSetting {...passProps} chartType={PIECHART} />,
    [BARCHART]: <ChartSetting {...passProps} chartType={BARCHART} />,
    [LINECHART]: <ChartSetting {...passProps} chartType={LINECHART} />,
    [SANKEY]: <SankeySetting {...passProps} {...crossDataProps} />,
    [TIMELINE]: <TimelineSetting {...passProps} {...crossDataProps} />,
    [CUSTOM]: <CustomSetting {...passProps} {...crossDataProps} />
  }
  const extraProps = { handleModalClose, loading }
  const extraComponents = {
    [TIMELINE]: <TimelineExtraSetting {...passProps} {...extraProps} {...crossDataProps} />
  }

  const dataSrcOpt = dataSrcList?.map(i => ({ value: i.id, label: i.name }))
  const tableOpt = Object.keys(rawSchema || {})?.map(i => ({ value: i, label: i }))

  return (
    <Modal title={`${ln_t('util.add')} ${label}`} open={show} footer={null} onCancel={() => handleModalClose()} width="70%" style={{ top: '32px', minWidth: '920px' }} destroyOnClose>
      <Form style={{ marginTop: '16px' }} form={form} layout="vertical" requiredMark={false} onFinish={e => handleSave(e)} >

        <SettingWrapper oneCol={(categoryDetail !== DATASOURCE && categoryDetail !== INDICATOR) || type === SANKEY || type === TAGS}>
          <BasicSettingWrapper style={{ padding: type === SANKEY ? 0 : '8px 16px 8px 0px' }}>
            <Form.Item {...defConfig('title', 'input')}>
              <Input placeholder={defPlaceholder('title', 'input')} />
            </Form.Item>

            {conf?.useCategoryDetail?.includes(type) &&
              <Form.Item {...defConfig('source', 'select')} name="categoryDetail" style={categoryDetail === DATASOURCE && conf.chartSchema !== PII_TABLE ? { marginBottom: '10px' } : {}}>
                  <Radio.Group
                    buttonStyle="solid"
                    optionType="button"
                    value={categoryDetail}
                    onChange={e => handleCatDetailChange(e.target.value)}
                    options={(type === SUMMARY ? [DATASOURCE, INDICATOR] : conf.categoryDetailOptions).map(i => ({ value: i, label: categoryDetailLabel[i] }))}
                  />
                </Form.Item>}
            {conf.useCrossData.includes(type) && (!conf?.useCategoryDetail?.includes(type) || categoryDetail === DATASOURCE) &&
              <Checkbox checked={crossData} onChange={handleCheckCrossData} style={{ marginBottom: '24px' }}>
                {ln_t('dashboard.crossDataCalculation')}
                <Tooltip title={ln_t('dashboard.crossDataInfo')}><QuestionCircleOutlined style={{ marginLeft: '4px' }} /></Tooltip>
              </Checkbox>}

            {conf.useDataSource.includes(type) && !crossData && categoryDetail === DATASOURCE && conf.chartSchema !== PII_TABLE && type !== TIMELINE &&
              <>
                <Form.Item {...defConfig('dataSource', 'select')}>
                  <Select
                    showSearch
                    placeholder={defPlaceholder('dataSource', 'select')}
                    options={dataSrcOpt}
                    optionFilterProp="children"
                    onChange={e => handleDataSrcChange(e)}
                  />
                </Form.Item>
                <Form.Item {...defConfig('tableName', 'select')}>
                  <Select
                    showSearch
                    placeholder={defPlaceholder('tableName', 'select')}
                    options={tableOpt}
                    optionFilterProp="children"
                    onChange={e => handleTableChange(e)}
                    disabled={_.isEmpty(rawSchema)}
                  />
                </Form.Item>
              </>}

            {categoryDetail === THIRD_PARTY
              ? <Form.Item {...defConfig('type', 'select')} name="tableName">
                  <Select
                    showSearch
                    placeholder={defPlaceholder('type', 'select')}
                    options={chtOptions(category)?.[type]?.map(i => ({ value: i, label: chtOptionsLabel[i] }))}
                    optionFilterProp="children"
                  />
                </Form.Item>
              : categoryDetail === TAXONOMY && type !== TAGS ? OrderAndLimit : ''}

            <StyleSetting defConfig={defConfig} type={type} init={editData} form={form} />
          </BasicSettingWrapper>

          <CustomSettingWrapper style={{ padding: type === SANKEY ? 0 : '8px 0px 8px 16px' }}>
            {crossData && crossDataStep === 1 && <CrossDataSetting {...crossDataProps} />}
            {categoryDetail === DATASOURCE && (!crossData || crossDataStep === 2) ? <div style={{ minHeight: '100px' }}>{components[type]}</div> : ''}
            {categoryDetail === INDICATOR && <IndicatorSetting {...passProps} {...indicatorProps} />}
            {!conf.hideFormActionBtn.includes(type) && <FormButton>
              {crossDataStep === 2
                ? <Button type="default" onClick={handleCrossDataPrevious}>{ln_t('util.previous')}</Button>
                : <Button type="default" onClick={() => handleModalClose()}>{ln_t('util.cancel')}</Button>}
              {crossData && crossDataStep === 1 ? '' : <Button type="primary" htmlType="submit" loading={loading}>{ln_t('util.confirm')}</Button>}
              {crossData && crossDataStep === 1 ? <Button type="primary" onClick={handleCrossDataNext}>{ln_t('util.next')}</Button> : ''}
            </FormButton>}
          </CustomSettingWrapper>
        </SettingWrapper>
        {extraComponents[type]}
      </Form>
    </Modal>
  )
}

export default SettingModal
